import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

type CustomPaginationProps = {
  limit: number
  setLimit: (limit: number) => void
  page: number
  setPage: (page: number) => void
  totalPages: number
  title?: string
  totalRecords: number
}

const getPageNumbers = (
  current: number,
  total: number
): (number | 'ellipsis')[] => {
  const delta = 1
  const range: number[] = []
  const rangeWithDots: (number | 'ellipsis')[] = []
  let last: number | undefined

  for (let i = 1; i <= total; i++) {
    if (
      i === 1 ||
      i === total ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i)
    }
  }

  for (const page of range) {
    if (last !== undefined) {
      if (page - last === 2) {
        rangeWithDots.push(last + 1)
      } else if (page - last > 2) {
        rangeWithDots.push('ellipsis')
      }
    }
    rangeWithDots.push(page)
    last = page
  }

  return rangeWithDots
}

export const CustomPagination: React.FC<CustomPaginationProps> = ({
  limit,
  setLimit,
  page,
  setPage,
  totalPages,
  totalRecords,
  title,
}) => {
  return (
    <div className='flex flex-col gap-4 border-t bg-background px-4 py-3 md:flex-row md:items-center md:justify-between'>
      {/* Left Section */}
      <div className='flex flex-wrap items-center gap-4'>
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <span>Rows per page</span>

          <Select
            value={String(limit)}
            onValueChange={value => {
              setLimit(Number(value))
              setPage(1)
            }}
          >
            <SelectTrigger className='h-8 w-[75px]'>
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              {[10, 20, 30, 50, 100].map(size => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <span className='text-sm text-muted-foreground'>
          {Math.min((page - 1) * limit + 1, totalRecords)}-
          {Math.min(page * limit, totalRecords)} of {totalRecords} &nbsp;
          {title}
        </span>
      </div>

      {/* Right Section */}
      <Pagination className='mx-0 w-auto justify-end'>
        <PaginationContent className='gap-1'>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => setPage(Math.max(page - 1, 1))}
              className={cn(
                'h-8 cursor-pointer',
                page === 1 && 'pointer-events-none opacity-50'
              )}
            />
          </PaginationItem>

          {getPageNumbers(page, totalPages).map(p => (
            <PaginationItem key={String(p)}>
              {p === 'ellipsis' ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  isActive={page === p}
                  onClick={() => setPage(p)}
                  className={cn(
                    'h-8 w-8 rounded-md border-none',
                    page === p
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  )}
                >
                  {p}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              onClick={() => setPage(Math.min(page + 1, totalPages))}
              className={cn(
                'h-8 cursor-pointer',
                page === totalPages && 'pointer-events-none opacity-50'
              )}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
}
